import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit2, Trash2, FileText, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const JustificativasFaltaPage = () => {
  const [justificativas, setJustificativas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    requer_documento: true,
    ativo: true
  });

  useEffect(() => {
    carregarJustificativas();
  }, []);

  const carregarJustificativas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/justificativas-falta?apenas_ativas=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJustificativas(response.data);
    } catch (error) {
      console.error('Erro ao carregar justificativas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editando) {
        await axios.put(
          `${API}/api/justificativas-falta/${editando.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/api/justificativas-falta`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      setEditando(null);
      setFormData({ nome: '', descricao: '', requer_documento: true, ativo: true });
      carregarJustificativas();
    } catch (error) {
      console.error('Erro ao salvar justificativa:', error);
      alert('Erro ao salvar justificativa');
    }
  };

  const handleEditar = (justificativa) => {
    setEditando(justificativa);
    setFormData({
      nome: justificativa.nome,
      descricao: justificativa.descricao || '',
      requer_documento: justificativa.requer_documento,
      ativo: justificativa.ativo
    });
    setShowModal(true);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente desativar esta justificativa?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/justificativas-falta/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      carregarJustificativas();
    } catch (error) {
      console.error('Erro ao excluir justificativa:', error);
      alert('Erro ao excluir justificativa');
    }
  };

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: '', descricao: '', requer_documento: true, ativo: true });
    setShowModal(true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Justificativas de Falta</h1>
          <p className="text-gray-600 mt-1">
            Gerencie as justificativas disponíveis para abonos de falta
          </p>
        </div>
        <Button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Nova Justificativa
        </Button>
      </div>

      {/* Lista de Justificativas */}
      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {justificativas.map((just) => (
            <Card key={just.id} className={!just.ativo ? 'opacity-50' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    {just.nome}
                  </span>
                  {just.ativo ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Ativo
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      Inativo
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {just.descricao && (
                  <p className="text-sm text-gray-600 mb-3">{just.descricao}</p>
                )}
                
                <div className="flex items-center gap-2 text-sm mb-4">
                  {just.requer_documento ? (
                    <span className="flex items-center gap-1 text-orange-600">
                      <FileText className="h-4 w-4" />
                      Requer documento
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-500">
                      <X className="h-4 w-4" />
                      Sem documento
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditar(just)}
                    className="flex-1"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  {just.ativo && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExcluir(just.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Justificativa' : 'Nova Justificativa'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome da Justificativa *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Atestado Médico"
                required
              />
            </div>

            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descreva quando esta justificativa deve ser usada..."
                className="w-full px-3 py-2 border rounded-md min-h-[80px]"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requer_documento"
                checked={formData.requer_documento}
                onChange={(e) => setFormData({...formData, requer_documento: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="requer_documento" className="cursor-pointer">
                Requer anexo de documento comprobatório
              </Label>
            </div>

            {editando && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="ativo" className="cursor-pointer">
                  Justificativa ativa
                </Label>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Check className="h-4 w-4 mr-2" />
                {editando ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JustificativasFaltaPage;
